import { Box, createStyles, makeStyles, Typography } from '@material-ui/core';
import React from 'react';
import { useTranslation } from '../../../../hooks/useTranslation';
import AccountStatusIcon from '../../../../static/Account_Status.svg';
import HistoryIcon from '../../../../static/outageIconsV2/history.svg';
import PDFIcon from '../../../../static/outageIconsV2/pdf.svg';
import colors from '../../../../themes/main-colors';
import theme from '../../../../themes/theme';
import { FullDivider } from '../../../pge-plus-common/FullDivider';
import { useOutageLeftPanelState } from '../../providers/OutageLeftPanelStateProvider';
import { CustomDialog } from '../common/custom-dialog';
import CottageIcon from '../icons/CottageIcon';
import { useOutageHistory } from './useOutageHistory';
import { Loader } from '../../../my-programs/components/Loader';
import { OutageHistoryItem } from './OutageHistoryItem';
import { useErrorNotification } from '../common/error-notification';

const useStyles = makeStyles(() =>
  createStyles({
    dialogContent: {
      scrollbarWidth: 'none',
      overflowY: 'auto',
      maxHeight: '600px',
    },
    contentSubText: {
      display: 'flex',
      flexDirection: 'column',
      background: colors.white,
    },
    helpIcon: {
      fontSize: 30,
      color: colors.white,
    },
    textMatterStrong: {
      fontSize: 18,
      fontWeight: 600,
    },
    textMatterLight: {
      fontSize: 16,
    },
    powerStatusOn: {
      color: colors.greenCheckBg,
    },
    powerStatusOff: {
      color: colors.errorRed,
    },
    flexBetween: {
      display: 'flex',
      flex: 1,
      justifyContent: 'space-between',
    },
    centerAlign: {
      display: 'flex',
      alignItems: 'center',
    },
    mr2: {
      marginRight: theme.spacing(2),
    },
    accountInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      paddingTop: '1.5rem',
      paddingLeft: '1.5rem',
      paddingRight: '1.5rem',
    },
    insetContainer: {
      display: 'flex',
      background: colors.white,
      padding: '1.5rem',
    },
    noOutagesFound: {
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    pdfContainer: {
      marginTop: '1rem',
      cursor: 'pointer',
    },
    outagesHistoryList: {
      width: '100%',
    },
  }),
);

type OutageHistoryProps = {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export function OutageHistory({ open, setOpen }: OutageHistoryProps) {
  const { t, richT } = useTranslation();
  const classes = useStyles();
  const { selectedAccountForOutage } = useOutageLeftPanelState();
  const { outageHistory, isLoading, pdf, isError } = useOutageHistory();
  const { setErrorNotification } = useErrorNotification();

  const meterNumber = '1234'; //TODO: Update meter number implementation

  const handleClose = () => {
    setOpen(false);
  };

  const handleDownload = () => {
    console.log(pdf);
  };

  if (isError) {
    setErrorNotification();
    return <></>;
  }

  return (
    <CustomDialog
      open={open}
      onClose={handleClose}
      title={t('MY_OUTAGE_HISTORY')}
      titleIcon={<HistoryIcon className={classes.helpIcon} />}
    >
      <Box className={classes.dialogContent}>
        {isLoading && <Loader />}
        {!isLoading && (
          <>
            <Box className={classes.contentSubText}>
              <Box className={classes.accountInfo}>
                <CottageIcon
                  width={32}
                  height={32}
                  color={
                    selectedAccountForOutage?.hasKnownOutage
                      ? colors.roseRed
                      : colors.pacificBlue
                  }
                />
                <Box>
                  {selectedAccountForOutage?.isPrimaryAccount && (
                    <Typography variant="body2">{t('PRIMARY')}</Typography>
                  )}
                  <Typography variant="h6" className={classes.textMatterStrong}>
                    {selectedAccountForOutage?.accountNumber}
                  </Typography>
                </Box>
              </Box>
              <Box paddingX={'1.5rem'} paddingBottom={'1rem'}>
                <Typography variant="body2" className={classes.textMatterLight}>
                  {selectedAccountForOutage?.addressLocation}
                </Typography>
              </Box>
              <FullDivider
                style={{
                  margin: 'unset',
                }}
              />
              <Box paddingX={'1.5rem'}>
                <Typography variant="body2" className={classes.textMatterLight}>
                  {richT('MY_OUTAGE_HISTORY_SUBTEXT')}
                </Typography>
              </Box>
            </Box>
            <Box paddingX={'1.5rem'} className={classes.flexBetween}>
              <Typography className={classes.textMatterStrong}>
                {richT('METER_NUMBER', {
                  METER_NUMBER: meterNumber ?? undefined,
                })}
              </Typography>
              <Box className={classes.centerAlign}>
                <Typography
                  className={classes.mr2 + ' ' + classes.textMatterStrong}
                >
                  {t('OUTAGE_POWER_ON')}
                </Typography>
                <AccountStatusIcon />
              </Box>
            </Box>
            <Box className={classes.insetContainer} marginX={'1.5rem'}>
              {outageHistory?.length === 0 && (
                <Box className={classes.noOutagesFound}>
                  <Typography
                    variant="body2"
                    className={classes.textMatterLight}
                  >
                    {richT('NO_OUTAGE_HISTORY', {
                      MONTHS: '3',
                    })}
                  </Typography>
                </Box>
              )}
              <Box className={classes.outagesHistoryList}>
                {outageHistory?.map((item, i) => (
                  <>
                    <OutageHistoryItem
                      key={i}
                      cause={item?.cause || ''}
                      duration={item?.duration || ''}
                      isPartialOutage={item?.isPartialOutage || false}
                      outageDate={item?.outageDate || ''}
                    />
                    {i !== outageHistory.length - 1 && <FullDivider />}
                  </>
                ))}
              </Box>
            </Box>
            <Box
              className={`${classes.insetContainer} ${classes.pdfContainer}`}
              onClick={handleDownload}
            >
              <Box marginRight={'1rem'}>
                <PDFIcon />
              </Box>
              <Typography variant="body2" className={classes.textMatterLight}>
                {t('DOWNLOAD_OUTAGE_HISTORY_PDF')}
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </CustomDialog>
  );
}
